import { useEffect } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Home, LayoutGrid, CreditCard, Headphones, User, Menu, Mic, Shield, Network, Languages, Moon, Sun } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { ReadAloudBar } from "@/components/ReadAloudBar";
import { VoiceAssistant } from "@/components/VoiceAssistant";
import { NotificationBell } from "@/components/NotificationBell";

const TABS = [
  { to: "/", key: "home", icon: Home, end: true },
  { to: "/services", key: "services", icon: LayoutGrid },
  { to: "/payments", key: "payments", icon: CreditCard },
  { to: "/support", key: "support", icon: Headphones },
  { to: "/profile", key: "profile", icon: User },
];

const SideMenu = () => {
  const { t, lang, setLang, theme, setTheme } = useApp();
  const item = "flex min-h-[48px] items-center gap-3 rounded-xl px-3 text-sm font-medium hover:bg-muted";
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button data-testid="header-menu-btn" aria-label="Menu" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10"><Menu size={20} /></button>
      </SheetTrigger>
      <SheetContent side={lang === "ar" ? "right" : "left"} className="w-72">
        <SheetHeader><SheetTitle className="text-start text-primary">{t("appName")}</SheetTitle></SheetHeader>
        <nav className="mt-6 space-y-1">
          <Link to="/requests" className={item} data-testid="menu-my-requests-link"><LayoutGrid size={18} /> {t("myRequests")}</Link>
          <Link to="/architecture" className={item} data-testid="menu-architecture-link"><Network size={18} /> {t("architecture")}</Link>
          <Link to="/admin" className={item} data-testid="menu-admin-link"><Shield size={18} /> {t("adminPortal")}</Link>
          <button className={`${item} w-full`} data-testid="menu-lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Languages size={18} /> {lang === "ar" ? "English" : "العربية"}</button>
          <button className={`${item} w-full`} data-testid="menu-theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />} {theme === "dark" ? t("light") : t("dark")}</button>
        </nav>
        <p className="mt-8 rounded-xl bg-[hsl(var(--gold-soft))] p-3 text-xs text-muted-foreground">{t("sandbox")}</p>
      </SheetContent>
    </Sheet>
  );
};

const DesktopAside = () => {
  const { t } = useApp();
  return (
    <aside className="hidden max-w-sm flex-col justify-center gap-6 px-10 text-white lg:flex">
      <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 px-3 py-1 text-xs tracking-wider text-[#E9CB75]">SANDBOX · ONE MOBILE APP</span>
      <h1 className="text-5xl font-extrabold leading-tight">Qatar One<br /><span className="text-[#E9CB75]">500 services.</span></h1>
      <p className="text-sm leading-relaxed text-white/75">{t("tagline")}. English & العربية, voice-first assistant in 12 languages, read-aloud on every screen and a 7-step request workflow.</p>
      <div className="flex gap-3 text-sm">
        <Link to="/architecture" className="rounded-full border border-white/30 px-4 py-2 font-semibold hover:bg-white/10" data-testid="aside-architecture-link">{t("architecture")}</Link>
        <Link to="/admin" className="rounded-full bg-[#B8901F] px-4 py-2 font-semibold hover:bg-[#997719]" data-testid="aside-admin-link">{t("adminPortal")}</Link>
      </div>
      <p className="text-[11px] text-white/50">All data is simulated demo data. No connector talks to a real government system.</p>
    </aside>
  );
};

export default function MobileShell() {
  const { t, lang, setLang, setAssistantOpen } = useApp();
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <div className="q1-pattern flex min-h-screen justify-center lg:items-start lg:gap-10 lg:py-8">
      <DesktopAside />
      <div className="relative flex min-h-screen w-full max-w-md flex-col bg-background shadow-2xl lg:h-[calc(100vh-4rem)] lg:min-h-0 lg:overflow-hidden lg:rounded-[2rem] lg:border-[6px] lg:border-[#2a0612]" data-testid="mobile-shell">
        <header className="q1-serrated sticky top-0 z-30 flex items-center gap-1 px-2 py-2 text-white">
          <SideMenu />
          <Link to="/" className="flex-1 text-center text-lg font-bold tracking-tight" data-testid="header-title">{t("appName")}</Link>
          <button data-testid="header-lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="h-11 min-w-[44px] rounded-full px-2.5 text-sm font-bold hover:bg-white/10" aria-label="Switch language">{lang === "ar" ? "EN" : "ع"}</button>
          <NotificationBell />
        </header>
        <ReadAloudBar />
        <main className="q1-scroll flex-1 pb-40 lg:overflow-y-auto" key={loc.pathname}><Outlet /></main>
        <button data-testid="voice-assistant-fab" onClick={() => setAssistantOpen(true)} aria-label={t("assistant")}
          className="fixed bottom-[76px] z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#B8901F] text-white shadow-xl ring-4 ring-background transition-transform hover:scale-105 ltr:right-[max(1rem,calc(50%-13rem))] rtl:left-[max(1rem,calc(50%-13rem))] lg:absolute lg:bottom-20 lg:ltr:right-4 lg:rtl:left-4">
          <Mic size={20} />
        </button>
        <nav className="fixed bottom-0 left-0 right-0 z-30 mx-auto flex max-w-md items-stretch justify-around border-t bg-background/95 px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:absolute" aria-label="Main">
          {TABS.map(({ to, key, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`tab-${key}`}
              className={({ isActive }) => `flex min-h-[60px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              {({ isActive }) => (<><Icon size={21} strokeWidth={isActive ? 2.4 : 1.8} /><span>{t(key)}</span>{isActive && <span className="mt-0.5 h-1 w-5 rounded-full bg-primary" />}</>)}
            </NavLink>
          ))}
        </nav>
        <VoiceAssistant />
      </div>
    </div>
  );
}
